import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Select, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { bookDiscoveryCall, getAvailability, getBookingDefaults } from '@/data/client';
import { useMembers } from '@/data/hooks';
import { useMe } from '@/auth/useMe';
import { ApiError } from '@/data/api';
import type { Prospect } from '@/data/types';
import { FormError, Modal } from '@/components/crm/Modal';
import { ConnectGoogleButton, useGoogleStatus } from '@/components/crm/GoogleConnect';
import { groupByDay } from '@/components/crm/ScheduleMeeting';

const LENGTHS = [15, 30, 45, 60].map((m) => ({ value: String(m), label: m < 60 ? `${m} min` : '1 hr' }));

/* The cold call's goal: a discovery call with the host (Matt unless the rep
   picks someone else). Slots are when the rep and the host are both free; the
   invite goes from the rep's Google Calendar to the host and the guests. Booking
   turns the prospect into a Cold call lead with a deal in the discovery stage. */
export function BookDiscoveryModal({ p, onClose }: { p: Prospect; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { me } = useMe();
  const { members } = useMembers();
  const { data: google } = useGoogleStatus();
  const { data: defaults } = useQuery({ queryKey: ['bookingDefaults'], queryFn: getBookingDefaults, staleTime: 5 * 60_000 });
  const [host, setHost] = useState('');
  useEffect(() => { if (!host && defaults?.hostUserId) setHost(defaults.hostUserId); }, [defaults, host]);
  const [duration, setDuration] = useState(30);
  const [slot, setSlot] = useState<string | null>(null);
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', phone: p.phone ?? '', note: '' });
  const [guests, setGuests] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const hostName = members.find((m) => m.userId === host)?.name ?? 'your colleague';

  const avail = useQuery({
    // Quarter-hour starts: a 9:15 or 9:45 discovery call is as bookable as a 9:30.
    queryKey: ['availability', [host], duration, 15],
    queryFn: () => getAvailability({ attendeeUserIds: host && host !== me?.userId ? [host] : [], durationMin: duration, days: 10, stepMin: 15 }),
    enabled: !!google?.connected && !!host,
    staleTime: 60_000,
  });
  const book = useMutation({
    mutationFn: () => bookDiscoveryCall(p.id, {
      hostUserId: host, startIso: slot!, durationMin: duration, firstName: f.firstName || undefined, lastName: f.lastName || undefined,
      email: f.email, phone: f.phone || undefined, guestEmails: guests, note: f.note || undefined,
    }),
    onSuccess: (r) => {
      for (const k of ['prospect', 'prospects', 'prospectSummary', 'leads', 'leadSummary', 'deals', 'companies', 'meetings']) void qc.invalidateQueries({ queryKey: [k] });
      navigate(r.dealId ? `/deals/${r.dealId}` : `/companies/${r.companyId}`);
    },
  });
  const addGuest = () => {
    const e = draft.trim().toLowerCase();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) && !guests.includes(e)) setGuests([...guests, e]);
    setDraft('');
  };
  const tz = avail.data?.timezone ?? me?.workspace.timezone ?? 'America/Chicago';
  const byDay = useMemo(() => groupByDay(avail.data?.slots ?? [], tz), [avail.data, tz]);
  const unreadable = avail.data?.people.filter((x) => x.error) ?? [];
  const needsGoogle = (google && !google.connected) || (avail.error instanceof ApiError && avail.error.code === 'google_not_connected');
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());

  return (
    <Modal title={`Book a discovery call — ${p.name}`} wide onClose={onClose} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={() => book.mutate()} disabled={!slot || !host || !emailOk || book.isPending} iconLeft={<Icon name="send" size={15} />}>
        {book.isPending ? 'Booking…' : 'Book and send invites'}
      </Button>
    </>}>
      {needsGoogle ? (
        <div className="crm-form">
          <p>The call is created on your Google Calendar, and Google sends the invitations. Connect your Triton Google account first.</p>
          <div><ConnectGoogleButton /></div>
        </div>
      ) : (
        <div className="crm-form">
          <div className="crm-grid2">
            <Select label="Call with" placeholder="Choose…" value={host} onChange={(e) => { setHost(e.target.value); setSlot(null); }}
              options={members.filter((m) => m.userId !== me?.userId).map((m) => ({ value: m.userId, label: `${m.name ?? m.email}${m.userId === defaults?.hostUserId ? ' (default)' : ''}` }))} />
            <Select label="Length" options={LENGTHS} value={String(duration)} onChange={(e) => { setDuration(Number(e.target.value)); setSlot(null); }} />
            <Input label="First name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} />
            <Input label="Last name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} />
            <Input label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} hint="The invite goes here" />
            <Input label="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </div>

          <div className="crm-chips-field">
            <span className="tr-field__label">Other guests <span className="crm-cell-sub">· optional</span></span>
            <div className="crm-chips">
              {guests.map((e) => (
                <button key={e} type="button" className="crm-chip is-on" onClick={() => setGuests(guests.filter((x) => x !== e))} title="Remove">{e} <Icon name="x" size={12} /></button>
              ))}
              <input className="crm-chip-input" type="email" placeholder="+ add email" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={addGuest}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addGuest(); } }} />
            </div>
          </div>

          <div className="crm-chips-field">
            <span className="tr-field__label">When <span className="crm-cell-sub">· times in {tz.replace('_', ' ')} · when you and {hostName} are both free</span></span>
            {avail.isLoading && <p className="crm-cell-muted">Checking calendars…</p>}
            {unreadable.length > 0 && <p className="crm-callout">Couldn't read {unreadable.map((x) => x.name ?? x.email).join(', ')}'s calendar — their busy times aren't included.</p>}
            <div className="crm-slots">
              {byDay.map(([day, slots]) => (
                <div key={day} className="crm-slots__day">
                  <strong>{day}</strong>
                  {slots.map((s) => <button key={s.iso} type="button" className={`crm-slot ${slot === s.iso ? 'is-on' : ''}`} onClick={() => setSlot(s.iso)}>{s.label}</button>)}
                </div>
              ))}
              {avail.data && !byDay.length && <p className="crm-cell-muted">No shared free time in the next two weeks.</p>}
            </div>
          </div>

          <Textarea label="Notes from the call (internal)" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          <p className="crm-cell-sub">
            Booking makes {p.name} a Cold call lead with a deal in {defaults?.stageName ?? 'the first pipeline stage'}, owned by {hostName}.
            The invite says: "A {duration}-minute call with {hostName} from Triton Commerce."
          </p>
          <FormError error={avail.error && !needsGoogle ? avail.error : book.error} />
        </div>
      )}
    </Modal>
  );
}
